import React, { useState, useEffect, useCallback } from 'react';
import { getNotes, addNote, editNote, deleteNote } from '../services/api';

// Option 2: notes. Persisted server-side so they double as a coaching record an
// admin can review later, and so a trainee never loses them to a refresh.
// The agent/call/lead in view is captured with each note for context.
export default function NotesPanel({ agent, context }) {
  const [notes, setNotes]   = useState([]);
  const [draft, setDraft]   = useState('');
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState('');
  const [editing, setEditing]   = useState(null);
  const [editText, setEditText] = useState('');

  const load = useCallback(async () => {
    try { setNotes(await getNotes()); } catch (_) { /* transient */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setBusy(true); setErr('');
    try {
      await addNote({
        note: text,
        agent_id:   agent?.id || null,
        agent_name: agent ? (agent.full_name || agent.username) : null,
        call_id:    context?.call_id || null,
        lead_id:    context?.lead?.id || null,
      });
      setDraft('');
      await load();
    } catch (e2) {
      setErr(e2.response?.data?.error || "Impossible d'enregistrer la note.");
    } finally {
      setBusy(false);
    }
  };

  const commitEdit = async (id) => {
    const text = editText.trim();
    if (!text) return;
    try {
      await editNote(id, text);
      setEditing(null); setEditText('');
      await load();
    } catch (_) { setErr("Impossible de modifier la note."); }
  };

  const remove = async (id) => {
    try { await deleteNote(id); await load(); }
    catch (_) { setErr("Impossible de supprimer la note."); }
  };

  // Ctrl/Cmd+Enter saves without leaving the keyboard mid-call.
  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') save(e);
  };

  return (
    <div className="tr-panel">
      <div className="tr-panel-head">
        <h3>Notes</h3>
        <span className="tr-count">{notes.length}</span>
      </div>

      <form className="tr-note-form" onSubmit={save}>
        <textarea
          className="tr-textarea"
          placeholder={agent
            ? `Notes pendant l'écoute de ${agent.full_name || agent.username}…`
            : 'Écrire une note…'}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          rows={4}
        />
        <div className="tr-note-actions">
          <span className="tr-hint">Ctrl/⌘ + Entrée pour enregistrer</span>
          <button className="tr-btn tr-btn-primary" disabled={busy || !draft.trim()}>
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
        {err && <div className="tr-login-err">{err}</div>}
      </form>

      <div className="tr-notes">
        {notes.length === 0 && <p className="tr-empty">Aucune note pour le moment.</p>}
        {notes.map(n => (
          <div className="tr-note" key={n.id}>
            {editing === n.id ? (
              <>
                <textarea className="tr-textarea" rows={3}
                          value={editText} onChange={e => setEditText(e.target.value)} />
                <div className="tr-note-actions">
                  <button type="button" className="tr-btn tr-btn-ghost"
                          onClick={() => setEditing(null)}>Annuler</button>
                  <button type="button" className="tr-btn tr-btn-primary"
                          onClick={() => commitEdit(n.id)}>Enregistrer</button>
                </div>
              </>
            ) : (
              <>
                <div className="tr-note-body">{n.note}</div>
                <div className="tr-note-meta">
                  {n.agent_name ? `${n.agent_name} · ` : ''}
                  {new Date(n.created_at).toLocaleString()}
                </div>
                <div className="tr-note-tools">
                  <button type="button" onClick={() => { setEditing(n.id); setEditText(n.note); }}>Modifier</button>
                  <button type="button" onClick={() => remove(n.id)}>Supprimer</button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
